// src/components/common/PhonePrefixSelector.jsx — copia por valor de apps/appointments (26/9/2026: fallback de WhatsApp del registro).
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, Check } from "lucide-react";
import countries from "../../data/countries.json";

const MENU_WIDTH = 288; // w-72
const MENU_MAX = 320;   // max-h-80

// `allowed` (opcional): lista cerrada de prefijos. Con uno solo se pinta fijo, sin desplegable
// (alta pública, 27/9/2026: solo +34). Sin `allowed`, todos los países (como en appointments).
const PhonePrefixSelector = ({ value, onChange, className = "", allowed }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState(null);
  const dropdownRef = useRef(null);     // wrapper (botón)
  const menuRef = useRef(null);         // menú en portal
  const searchInputRef = useRef(null);

  const selectedCountry = useMemo(() => {
    return (
      countries.find((c) => c.dial_code === value) ||
      countries.find((c) => c.code === "ES") // Default to Spain
    );
  }, [value]);

  const options = useMemo(
    () => (allowed?.length ? countries.filter((c) => allowed.includes(c.dial_code)) : countries),
    [allowed]
  );

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return options;
    const searchLower = search.toLowerCase().trim();
    return options.filter(
      (c) =>
        c.name.toLowerCase().includes(searchLower) ||
        c.dial_code.includes(searchLower) ||
        c.code.toLowerCase().includes(searchLower)
    );
  }, [search, options]);

  // Calcula la posición del menú en coordenadas de viewport (position: fixed) para
  // que NO lo recorte ningún contenedor con overflow (p. ej. modales). Decide si
  // se abre hacia arriba o hacia abajo según el espacio disponible.
  const computeCoords = useCallback(() => {
    const el = dropdownRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < MENU_MAX && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(MENU_MAX, (openUp ? spaceAbove : spaceBelow) - 12));
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8));
    setCoords({
      left,
      openUp,
      maxHeight,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
    });
  }, []);

  const handleToggle = () => {
    if (!isOpen) computeCoords();
    setIsOpen((o) => !o);
  };

  // Cerrar al hacer click fuera (botón + menú en portal).
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        menuRef.current && !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reposicionar al hacer scroll o redimensionar mientras está abierto.
  useEffect(() => {
    if (!isOpen) return;
    const reposition = () => computeCoords();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [isOpen, computeCoords]);

  // Focus al buscador al abrir.
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (country) => {
    onChange(country.dial_code);
    setIsOpen(false);
    setSearch("");
  };

  if (allowed?.length === 1) {
    const fixed = options[0] || { code: "", dial_code: allowed[0] };
    return (
      <div className={`relative ${className}`}>
        <span
          aria-label={`Prefijo ${fixed.dial_code}`}
          className="h-full flex items-center px-3 py-3 bg-gray-50 border border-gray-200 rounded-l-xl text-sm font-medium text-gray-700"
        >
          {fixed.code} {fixed.dial_code}
        </span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selector Button */}
      <button
        type="button"
        onClick={handleToggle}
        className="h-full flex items-center gap-2 px-3 py-3 bg-gray-50 border border-gray-200 rounded-l-xl hover:bg-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 min-w-[100px] justify-between"
      >
        <span className="text-sm font-medium text-gray-700">
          {selectedCountry?.code} {selectedCountry?.dial_code}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-gray-500 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown en portal (position: fixed) para escapar del overflow del modal */}
      {isOpen && coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              left: coords.left,
              width: MENU_WIDTH,
              maxHeight: coords.maxHeight,
              zIndex: 9999,
              ...(coords.openUp ? { bottom: coords.bottom } : { top: coords.top }),
            }}
            className="flex flex-col bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            {/* Search */}
            <div className="p-2 border-b border-gray-100 flex-none">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar país..."
                  className="w-full pl-8 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Country List */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {filteredCountries.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No se encontraron países
                </div>
              ) : (
                filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleSelect(country)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-purple-50 transition-colors ${
                      country.dial_code === value ? "bg-purple-50" : ""
                    }`}
                  >
                    <span className="w-8 text-xs font-semibold text-gray-500">
                      {country.code}
                    </span>
                    <span className="flex-1 text-sm text-gray-800 truncate">
                      {country.name}
                    </span>
                    <span className="text-sm font-medium text-purple-600">
                      {country.dial_code}
                    </span>
                    {country.dial_code === value && (
                      <Check className="w-3 h-3 text-purple-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PhonePrefixSelector;
